"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Link } from "@/components/common/Link";
import { MapPin, Mail, Phone, Clock, ExternalLink, Loader } from "lucide-react";
const PRACTICE_AREAS = ["Mixing & Mastering", "Beat Licensing", "Full Production", "Vocal Tuning", "Sound Design", "Consultation"];
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center rounded-md border border-border bg-card p-8 text-center">
        <div className="mb-4 flex size-12 items-center justify-center rounded-lg bg-primary text-primary-foreground">
          <svg className="size-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="mb-2 font-default text-xl font-semibold text-foreground">
          Transmission Received
        </h3>
        <p className="mb-6 text-muted-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="rounded-md border-foreground text-foreground hover:bg-foreground hover:text-background">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <FormField control={control} name="name" rules={{
      required: "Name is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-default uppercase tracking-widest text-xs">Full Name</FormLabel>
            <FormControl>
              <Input placeholder="Artist or Label Name" className="border-0 border-b border-border rounded-md bg-transparent px-0 focus-visible:ring-0 focus-visible:border-foreground transition-colors" {...field} />
            </FormControl>
            <FormDescription className="text-[10px] uppercase opacity-50">Identify yourself.</FormDescription>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default uppercase tracking-widest text-xs">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="contact@studio.com" className="border-0 border-b border-border rounded-md bg-transparent px-0 focus-visible:ring-0 focus-visible:border-foreground transition-colors" {...field} />
              </FormControl>
              <FormDescription className="text-[10px] uppercase opacity-50">For project correspondence.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default uppercase tracking-widest text-xs">Phone / WhatsApp</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (000) 000-0000" className="border-0 border-b border-border rounded-md bg-transparent px-0 focus-visible:ring-0 focus-visible:border-foreground transition-colors" {...field} />
              </FormControl>
              <FormDescription className="text-[10px] uppercase opacity-50">
                Direct line for urgent sessions.
              </FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="practice_area" rules={{
      required: "Select a service type"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-default uppercase tracking-widest text-xs">Project Type</FormLabel>
            <FormControl>
              <select className="border-0 border-b border-border focus-visible:ring-0 focus-visible:border-foreground dark:bg-transparent flex h-9 w-full min-w-0 rounded-md bg-transparent px-0 py-1 text-base transition-colors outline-none disabled:pointer-events-none disabled:opacity-50 md:text-sm" {...field}>
                <option value="" disabled className="bg-background">
                  Select service package
                </option>
                {PRACTICE_AREAS.map((area, i) => <option key={area} value={area} data-index={i} className="bg-background">
                    {area}
                  </option>)}
              </select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-default uppercase tracking-widest text-xs">Project Brief</FormLabel>
            <FormControl>
              <Textarea placeholder="Describe your sonic vision..." className="min-h-[120px] resize-y border-0 border-b border-border rounded-md bg-transparent px-0 focus-visible:ring-0 focus-visible:border-foreground transition-colors" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4">
          <p className="text-sm text-destructive">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-foreground text-background hover:bg-foreground/90 md:w-full rounded-md font-default uppercase tracking-widest py-8 text-lg" size="lg">
        {isSubmitting ? <>
            <Loader className="mr-2 size-5 animate-spin" />
            Processing...
          </> : "Initiate Collaboration"}
      </Button>
    </Form>;
}
export default function InitiateCollaboration() {
  const contactBlocks = [{
    icon: <Phone className="size-6 text-foreground" />,
    title: "Direct Line",
    content: "+1 (555) RHYTHM-01",
    action: <Link to="https://wa.me/155574984601" newTab className="mt-2 inline-flex items-center text-sm font-mono text-muted-foreground hover:text-foreground transition-colors">
          WhatsApp <ExternalLink className="ml-1 size-3" />
        </Link>
  }, {
    icon: <Mail className="size-6 text-foreground" />,
    title: "Studio Inquiries",
    content: "collab@rhythmsoul.pro",
    action: <Link to="mailto:collab@rhythmsoul.pro" className="mt-2 inline-flex items-center text-sm font-mono text-muted-foreground hover:text-foreground transition-colors">
          Send Email <ExternalLink className="ml-1 size-3" />
        </Link>
  }, {
    icon: <Clock className="size-6 text-foreground" />,
    title: "Studio Hours",
    content: "24/7 Session Availability",
    action: null
  }, {
    icon: <MapPin className="size-6 text-foreground" />,
    title: "Headquarters",
    content: "The Sound Lab, Los Angeles, CA",
    action: <Link to="https://maps.google.com/?q=Los+Angeles+Recording+Studios" newTab className="mt-3 inline-flex items-center justify-center rounded-md border border-border bg-background px-4 py-2 text-sm font-default uppercase tracking-widest text-foreground shadow-md transition-colors hover:bg-foreground hover:text-background">
          Locate Studio
        </Link>
  }];
  return <section data-section-id="224539" id="initiate-collaboration" className="bg-background py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-16 text-center lg:text-left">
          <h2 className="font-default text-5xl font-bold tracking-tighter text-foreground sm:text-7xl uppercase">
            Let's Build
          </h2>
          <p className="mt-4 max-w-2xl text-lg font-default text-muted-foreground lg:mx-0">
            Ready to define your sound? Our production suite is open for artists who demand sonic excellence. Reach out to start the session.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-16 lg:grid-cols-2 lg:gap-24">
          <div className="rounded-md border border-border bg-card p-6 shadow-lg sm:p-8">
            <FormProvider formKey="collaboration_form_home" formId="collaboration_form_home" sectionName="Initiate Collaboration" defaultValues={{
            name: "",
            email: "",
            phone: "",
            practice_area: "",
            message: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true
            },
            phone: {
              required: true
            },
            practice_area: {
              required: true
            },
            message: {
              required: true
            }
          }} submitText="Initiate Collaboration" successMessage="Your request has been logged. Our production team will contact you within 24 hours.">
              <ContactFormInner />
            </FormProvider>
          </div>

          <div className="flex flex-col justify-center space-y-10">
            {contactBlocks.map((block, i) => <div key={i} data-index={i} className="flex items-start gap-6">
                <div className="flex size-12 shrink-0 items-center justify-center rounded-md bg-accent/20 border border-border">
                  {block.icon}
                </div>
                <div>
                  <h3 className="font-default text-lg font-bold uppercase tracking-widest text-foreground">
                    {block.title}
                  </h3>
                  <p className="mt-1 text-base font-mono text-muted-foreground">
                    {block.content}
                  </p>
                  {block.action}
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}